import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Text } from '@library';
import ProductStockIndicator from '../../../../availability/components/ProductStockIndicator';
import HorizontalLayout from '../../../../core/components/layout/HorizontalLayout';
import VerticalLayout from '../../../../core/components/layout/VerticalLayout';
import CurrentStoreLink from '../../../../core/components/router/CurrentStoreLink';
import useTabletView from '../../../../core/hooks/dom/useTabletView';
import ProductMyHEKPrice from '../../ProductTileView/ProductMyHEKPrice';
import ProductRetailPrice from '../../ProductTileView/ProductRetailPrice';
import ProductTileActions from '../../ProductTileView/ProductTileActions';
import type {
    LoggedInProductCustomerPrice,
    ReplacementProductDetails,
} from '../../../types';
import { useProductPrice } from '../../../../pricing/queries/useProductPrice';
import ProductImage from '../../ProductImage/ProductImage';
import useCustomerPriceVisibility from '../../../../core/hooks/useCustomerPriceVisibility';
import styles from './ReplacementProducts.module.scss';
import ReplacementProductTabletViewRetailPrice from './ReplacementProductTabletViewRetailPrice';
import ReplacementProductTabletViewMyHEKPrice from './ReplacementProductTabletViewMyHEKPrice';

const ReplacementProductsDropdownItem = ({
    noSpacers = false,
    product,
}: {
    noSpacers?: boolean;
    product: ReplacementProductDetails;
}) => {
    const [showCustomerPrice] = useCustomerPriceVisibility();
    const isTabletView = useTabletView();
    const { loading, customerPrice } = useProductPrice({
        product,
    });
    const hideSpacers = isTabletView || noSpacers;

    return (
        <>
            {!hideSpacers && <div />}
            <CurrentStoreLink to={`/product/${product.id}`}>
                <HorizontalLayout
                    align="start"
                    gap={isTabletView ? 'micro' : 'medium'}
                >
                    <ProductImage
                        size="100px"
                        url={product.media[0]?.renditionUrl}
                    />

                    <div className={styles.productPrimaryInfo}>
                        <VerticalLayout gap="micro">
                            <Text tertiary light>
                                <FormattedMessage id="Common ItemNumber" />
                                {': '}
                                {product.sku}
                            </Text>
                            <Text primary medium>
                                {product.name}
                            </Text>
                        </VerticalLayout>
                    </div>
                </HorizontalLayout>
            </CurrentStoreLink>
            <ProductStockIndicator
                vertical
                hideBranchesWithoutAvailability
                messageStyle="right"
                sku={product.sku}
            />
            <VerticalLayout justify="start" stretchChildren>
                {isTabletView ? (
                    <>
                        <ReplacementProductTabletViewRetailPrice
                            retailPrice={customerPrice?.retailPrice}
                            loading={loading}
                        />
                        <ReplacementProductTabletViewMyHEKPrice
                            pricingInfo={customerPrice}
                            loading={loading}
                        />
                    </>
                ) : (
                    <>
                        <ProductRetailPrice
                            retailPrice={customerPrice?.retailPrice}
                            loading={loading}
                        />
                        {showCustomerPrice && (
                            <ProductMyHEKPrice
                                hideDiscountTags
                                topAlignPrices
                                loading={loading}
                                pricingInfo={
                                    customerPrice as
                                        | LoggedInProductCustomerPrice
                                        | undefined
                                }
                                productWithAdditionalCharges={product}
                                product={product}
                            />
                        )}
                    </>
                )}
            </VerticalLayout>
            <div data-test-class="replacementProductItemActions">
                <ProductTileActions activeSKU={product.sku} />
            </div>
            {!hideSpacers && <div />}
        </>
    );
};

export default memo(ReplacementProductsDropdownItem);
